<template>
  <section class="settings-view">
    <div class="settings-hero">
      <div>
        <p class="settings-hero__eyebrow">ODIN / Configuración</p>
        <h1 class="settings-hero__title">Cuenta, acceso y operación</h1>
        <p class="settings-hero__copy">
          Centraliza los datos de la sesión activa, los permisos vigentes y las acciones de seguridad disponibles.
        </p>
      </div>
      <div class="settings-hero__badge">
        <span class="settings-hero__badge-label">Sesión</span>
        <strong>{{ user.name || user.email || 'Usuario activo' }}</strong>
      </div>
    </div>

    <div class="settings-grid">
      <article class="settings-card">
        <p class="settings-card__eyebrow">Notificaciones</p>
        <h2 class="settings-card__title">Correos del sistema</h2>
        <p class="settings-note">Configura tus preferencias de correo{{ canManageUsers ? ', los eventos y el resumen semanal de cumpleaños' : '' }}.</p>
        <router-link to="/settings/notifications" class="button settings-action">Configurar correos</router-link>
      </article>
      <article class="settings-card settings-card--highlight">
        <p class="settings-card__eyebrow">Cuenta</p>
        <h2 class="settings-card__title">Resumen del usuario</h2>
        <dl class="settings-definition">
          <div class="settings-definition__row">
            <dt>Nombre</dt>
            <dd>{{ user.name || 'Sin nombre registrado' }}</dd>
          </div>
          <div class="settings-definition__row">
            <dt>Email</dt>
            <dd>{{ user.email || 'Sin email registrado' }}</dd>
          </div>
          <div class="settings-definition__row">
            <dt>Perfiles</dt>
            <dd>{{ profiles.length }}</dd>
          </div>
          <div class="settings-definition__row">
            <dt>Permisos</dt>
            <dd>{{ permissions.length }}</dd>
          </div>
        </dl>
      </article>

      <article class="settings-card">
        <p class="settings-card__eyebrow">Acceso</p>
        <h2 class="settings-card__title">Perfiles asignados</h2>
        <div v-if="profiles.length" class="settings-pill-list">
          <span v-for="profile in profiles" :key="profile.id || profile.description" class="settings-pill">
            {{ profile.description }}
          </span>
        </div>
        <p v-else class="settings-empty">No hay perfiles asociados a este usuario.</p>
      </article>

      <article class="settings-card">
        <p class="settings-card__eyebrow">Administración</p>
        <h2 class="settings-card__title">Acciones disponibles</h2>
        <p class="settings-note">
          {{ canManageUsers ? 'Este usuario puede administrar cuentas y perfiles.' : 'Este usuario no tiene permisos administrativos sobre cuentas.' }}
        </p>
        <router-link
          v-if="canManageUsers"
          to="/users"
          class="button settings-action"
        >
          Ir a usuarios
        </router-link>
      </article>
    </div>

    <div class="settings-layout">
      <article class="settings-panel">
        <div class="settings-panel__head">
          <div>
            <p class="settings-panel__eyebrow">Seguridad</p>
            <h2 class="settings-panel__title">Cambiar contraseña</h2>
          </div>
        </div>
        <div class="settings-panel__body">
          <changePassword />
        </div>
      </article>

      <article class="settings-panel">
        <div class="settings-panel__head">
          <div>
            <p class="settings-panel__eyebrow">Permisos vigentes</p>
            <h2 class="settings-panel__title">Matriz de acceso</h2>
          </div>
          <span class="settings-panel__counter">{{ permissions.length }}</span>
        </div>
        <div class="settings-panel__body">
          <div v-if="permissions.length" class="settings-permission-list">
            <article
              v-for="permission in permissions"
              :key="permission.id || permission.code"
              class="settings-permission"
            >
              <strong>{{ permission.description || permission.code }}</strong>
              <span>{{ permission.code }}</span>
            </article>
          </div>
          <p v-else class="settings-empty">No hay permisos cargados para esta sesión.</p>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import auth from '@/auth/index'
import changePassword from '@/components/changePassword.vue'

export default {
  name: 'SettingsView',
  components: {
    changePassword
  },
  data () {
    return {
      user: auth.getUserLogged() || {}
    }
  },
  computed: {
    profiles () {
      return this.user.profiles || []
    },
    permissions () {
      return this.user.permissions || []
    },
    canManageUsers () {
      return auth.canManageUsers()
    }
  }
}
</script>

<style scoped>
.settings-view {
  padding: 2rem;
}

.settings-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 2rem;
  border-radius: 32px;
  background:
    radial-gradient(circle at top right, rgba(232, 214, 183, 0.45), transparent 28%),
    linear-gradient(135deg, #241d17 0%, #4b3a29 100%);
  box-shadow: 0 28px 60px rgba(42, 31, 20, 0.16);
}

.settings-hero__eyebrow {
  margin: 0 0 0.75rem;
  color: rgba(247, 240, 229, 0.68);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.22rem;
  text-transform: uppercase;
}

.settings-hero__title {
  margin: 0;
  color: #fff7ea;
  font-size: clamp(2rem, 4vw, 3.3rem);
  line-height: 0.98;
}

.settings-hero__copy {
  max-width: 46rem;
  margin: 1rem 0 0;
  color: rgba(255, 248, 239, 0.8);
  font-size: 1rem;
  line-height: 1.75;
}

.settings-hero__badge {
  min-width: 240px;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff7ea;
}

.settings-hero__badge-label {
  display: block;
  margin-bottom: 0.35rem;
  color: rgba(255, 248, 239, 0.7);
  font-size: 0.72rem;
  letter-spacing: 0.14rem;
  text-transform: uppercase;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1.25rem;
}

.settings-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: 1rem;
  margin-top: 1rem;
}

.settings-card,
.settings-panel {
  border: 1px solid rgba(68, 55, 41, 0.08);
  border-radius: 26px;
  background:
    linear-gradient(180deg, rgba(255, 252, 247, 0.98), rgba(247, 240, 230, 0.94));
  box-shadow: 0 18px 38px rgba(58, 44, 29, 0.08);
}

.settings-card {
  padding: 1.5rem;
}

.settings-card--highlight {
  background:
    radial-gradient(circle at top right, rgba(236, 221, 197, 0.9), transparent 34%),
    linear-gradient(180deg, rgba(255, 252, 247, 0.98), rgba(245, 235, 220, 0.96));
}

.settings-card__eyebrow,
.settings-panel__eyebrow {
  margin: 0 0 0.55rem;
  color: rgba(75, 58, 38, 0.56);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.18rem;
  text-transform: uppercase;
}

.settings-card__title,
.settings-panel__title {
  margin: 0;
  color: #211b15;
  font-size: 1.35rem;
}

.settings-definition {
  margin-top: 1rem;
}

.settings-definition__row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.7rem 0;
  border-top: 1px solid rgba(75, 58, 38, 0.08);
}

.settings-definition__row:first-child {
  border-top: 0;
  padding-top: 0;
}

.settings-definition__row dt {
  color: rgba(75, 58, 38, 0.62);
  font-weight: 600;
}

.settings-definition__row dd {
  margin: 0;
  color: #211b15;
  font-weight: 700;
  text-align: right;
}

.settings-pill-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 1rem;
}

.settings-pill {
  display: inline-flex;
  align-items: center;
  min-height: 2rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: rgba(234, 224, 209, 0.78);
  color: #57483a;
  font-size: 0.82rem;
  font-weight: 600;
}

.settings-note,
.settings-empty {
  margin: 1rem 0 0;
  color: #4c3d2f;
  line-height: 1.7;
}

.settings-action {
  margin-top: 1rem;
  min-height: 2.8rem;
  padding: 0 1rem;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #1f2937, #374151);
  color: #fff;
  box-shadow: 0 12px 28px rgba(31, 41, 55, 0.14);
}

.settings-action:hover,
.settings-action:focus {
  color: #fff;
}

.settings-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem 1.5rem 0;
}

.settings-panel__body {
  padding: 1.25rem 1.5rem 1.5rem;
}

.settings-panel__counter {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.4rem;
  height: 2.4rem;
  padding: 0 0.8rem;
  border-radius: 999px;
  background: #2d241c;
  color: #fff4e6;
  font-size: 0.9rem;
  font-weight: 700;
}

.settings-permission-list {
  display: grid;
  gap: 0.75rem;
}

.settings-permission {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.9rem 1rem;
  border: 1px solid rgba(75, 58, 38, 0.08);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.62);
}

.settings-permission strong {
  color: #241d17;
}

.settings-permission span {
  color: rgba(75, 58, 38, 0.6);
  font-size: 0.84rem;
  letter-spacing: 0.05em;
}

@media (max-width: 1023px) {
  .settings-view {
    padding: 1rem;
  }

  .settings-hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .settings-hero__badge {
    min-width: 0;
    width: 100%;
  }

  .settings-grid,
  .settings-layout {
    grid-template-columns: 1fr;
  }
}
</style>
